// Dashboard "Exe" section — commands the headed .exe agent and watches results.
// Fully isolated: only calls /exe/* (api/exe.js). Never touches /test or /extension.
// UI only — all exe API calls, state, and polling are unchanged.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu, Activity, Play, Square, Save, Search, Download, KeyRound, RefreshCw,
  Clock, CheckCircle2, XCircle, AlertTriangle, Bookmark, History,
  Loader2, User as UserIcon, Check, Server, Ban, CircleDot, Repeat, RotateCw, Wand2, X, Camera,
  FileSpreadsheet, Upload, Trash2, Pause, Rocket, Sparkles, ListChecks, FolderKanban,
  ChevronRight, ChevronDown, Pencil, Wifi, WifiOff, HardDrive, MemoryStick, Globe, Zap,
} from "lucide-react";
import * as XLSX from "xlsx";
import Aurora from "@/components/common/Aurora";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/shared/EmptyState";
import Modal from "@/components/dashboard/Modal";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import RerunBatchButton from "@/components/dashboard/RerunBatchButton";
import DevStatusCell from "@/components/dashboard/DevStatusCell";
import DeleteAllButton from "@/components/shared/DeleteAllButton";
import { cn } from "@/lib/cn";

import { authedBlobUrl } from "@/api/client";
import { getProjects } from "@/api/projects";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  cancelExeRun, compileExeRun, downloadAgent, exeScreenshotPath, setDevicePersonal,
  getExeInfo, getExeRun, isTerminalExe, listExeDevices,
  listExeTests, pingExeDevice, renameExeDevice, replayExeTest, rootCauseExeRun,
  startExeRun, detectExeColumns, importExeTestCases, loadAllExeTestCases, deleteExeTestCase,
  moveExeTestCases,
  createExeBatch, listExeBatches, getExeBatch, listExeBatchItems, listExeRuns,
  pauseExeBatch, resumeExeBatch, stopExeBatch, takeoverExeBatch, downloadExeBatchReport,
  getExeRerunPreview, rerunExeBatch,
  deleteAllExeTestCases, deleteAllExeBatches, refreshExeLogins,
  deleteAllExeTests,
} from "@/api/exe";
import { formatDateTime } from "@/lib/dates";

const TABS = [
  { key: "System Health", label: "System Health", icon: Activity },
  { key: "Run Single", label: "Run Single", icon: Play },
  { key: "Run batch", label: "Run batch", icon: Play },
  { key: "Running", label: "Running", icon: ListChecks },
  { key: "Re-run", label: "Re-run", icon: RotateCw },
  { key: "Saved Tests", label: "Saved Tests", icon: Bookmark },
  { key: "History", label: "History", icon: History },
];
const BROWSERS = ["chromium", "firefox", "edge"];

const FIELD =
  "w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500";

const fmt = (d) => formatDateTime(d);
// PC display name everywhere: custom name first, hostname in brackets — "QA laptop (DESKTOP-VKL2N4P)".
const devName = (d) =>
  d?.display_name ? `${d.display_name} (${d.label || d.id.slice(0, 8)})` : (d?.label || d?.id?.slice(0, 8) || "—");
const fmtGb = (mb) => (mb == null ? null : `${(mb / 1024).toFixed(mb >= 10240 ? 0 : 1)} GB`);
// Bytes → human size for the agent download (mirrors the extension download card).
const fmtBytes = (b) => {
  if (b == null) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};
const fmtDur = (s) =>
  s == null ? "—" : s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
const fmtCost = (tu) => {
  // Agents report cost under either name — accept both so it never shows "—".
  const c = tu && (tu.cost_usd ?? tu.estimated_cost_usd);
  if (c == null) return null;
  if (!c) return "free";
  return c < 0.01 ? `$${c.toFixed(4)}` : `$${c.toFixed(3)}`;
};
const fmtTokens = (tu) => {
  if (!tu) return null;
  const t = (tu.input ?? tu.total_input_tokens ?? 0) + (tu.output ?? tu.total_output_tokens ?? 0);
  return t ? `${t.toLocaleString()} tok` : null;
};
// Elapsed wall-clock as a compact "1h 4m" / "8m 12s" / "42s" string.
const fmtElapsed = (sec) => {
  if (sec == null || sec < 0) return null;
  const r = Math.round(sec);
  const h = Math.floor(r / 3600), m = Math.floor((r % 3600) / 60), s = r % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};
// How long the CURRENT run took: completed_at − started_at, or live (now − started_at)
// while it runs. `started_at` is reset on every re-run, so a re-run shows its own
// minutes, not the wall-clock since the batch was first created days ago. Falls back to
// created_at for batches created before started_at existed.
const batchElapsed = (b) => {
  const start = b && (b.started_at || b.created_at);
  if (!start) return null;
  const end = b.completed_at ? new Date(b.completed_at).getTime() : Date.now();
  return (end - new Date(start).getTime()) / 1000;
};

const STATUS_STYLE = {
  passed: { icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300", label: "Passed" },
  failed: { icon: XCircle, cls: "bg-rose-500/10 text-rose-600 dark:text-rose-300", label: "Failed" },
  error: { icon: AlertTriangle, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-300", label: "Error" },
  running: { icon: Loader2, cls: "bg-brand-500/10 text-brand-600 dark:text-brand-300", label: "Running", spin: true },
  cancelled: { icon: Square, cls: "bg-slate-500/10 text-slate-500 dark:text-slate-400", label: "Cancelled" },
  partial: { icon: CircleDot, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-300", label: "Partial" },
  blocked: { icon: Ban, cls: "bg-rose-500/10 text-rose-600 dark:text-rose-300", label: "Blocked" },
  pending: { icon: Clock, cls: "bg-slate-500/10 text-slate-500 dark:text-slate-400", label: "Pending" },
  queued: { icon: Clock, cls: "bg-slate-500/10 text-slate-500 dark:text-slate-400", label: "Queued" },
  requeued: { icon: Repeat, cls: "bg-slate-500/10 text-slate-500 dark:text-slate-400", label: "Requeued" },
  claimed: { icon: CircleDot, cls: "bg-brand-500/10 text-brand-600 dark:text-brand-300", label: "Claimed" },
  completed: { icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300", label: "Completed" },
  paused: { icon: Pause, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-300", label: "Paused" },
  stopped: { icon: Square, cls: "bg-slate-500/10 text-slate-500 dark:text-slate-400", label: "Stopped" },
  orphaned: { icon: AlertTriangle, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-300", label: "Needs attention" },
  inconclusive: { icon: AlertTriangle, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-300", label: "Inconclusive" },
};

function ExeStatus({ status }) {
  const s = STATUS_STYLE[String(status || "").toLowerCase()] || STATUS_STYLE.pending;
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", s.cls)}>
      <Icon size={13} className={s.spin ? "animate-spin" : undefined} /> {s.label}
    </span>
  );
}

function DeviceSelect({ value, onChange, devices, placeholder }) {
  return (
    <select value={value} onChange={onChange} className={FIELD}>
      <option value="">{devices.length ? placeholder : "No online agents"}</option>
      {devices.map((d) => <option key={d.id} value={d.id}>{devName(d)}</option>)}
    </select>
  );
}

const fade = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.25, ease: "easeOut" } };

export default function ExeRunner() {
  const [tab, setTab] = useState("System Health");
  // Survives a tab switch — see BLANK_RUN.
  const [runDraft, setRunDraft] = useState(BLANK_RUN);
  const [devices, setDevices] = useState([]);

  const refreshDevices = useCallback(async () => {
    try {
      setDevices(await listExeDevices());
    } catch (e) {
      /* surfaced per-tab */
    }
  }, []);

  useEffect(() => {
    refreshDevices();
    const t = setInterval(refreshDevices, 5000);
    return () => clearInterval(t);
  }, [refreshDevices]);

  const online = devices.filter((d) => d.status === "online").length;

  return (
    <>
      {/* hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-7"
      >
        <Aurora className="opacity-60" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
              <Cpu size={20} />
            </span>
            <div>
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">Desktop agent</p>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">Exe Agent</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Run real-browser tests on your own machines, headed and live.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 bg-white/70 px-3 py-1.5 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <Server size={13} className="text-brand-500" />
                  <span className="font-semibold text-slate-900 dark:text-white">{devices.length}</span>
                  <span className="text-slate-500 dark:text-slate-400">agents</span>
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 bg-white/70 px-3 py-1.5 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
                  <span className={cn("h-2 w-2 rounded-full", online ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/20")} />
                  <span className="font-semibold text-slate-900 dark:text-white">{online}</span>
                  <span className="text-slate-500 dark:text-slate-400">online</span>
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refreshDevices}>
              <RefreshCw size={16} /> Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* tabs */}
      <div className="mb-6 flex gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-soft dark:border-white/10 dark:bg-ink-900">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "relative flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors",
                active ? "text-white" : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
              )}
            >
              {active && (
                <motion.span
                  layoutId="exe-tab"
                  className="absolute inset-0 -z-0 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 shadow-soft"
                  transition={{ type: "spring", stiffness: 460, damping: 36 }}
                />
              )}
              <t.icon size={15} className="relative z-10" />
              <span className="relative z-10 whitespace-nowrap">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* tab content */}
      <AnimatePresence mode="wait">
        <motion.div key={tab} {...fade}>
          {tab === "System Health" && <SystemHealth devices={devices} onPing={pingExeDevice} onRenamed={refreshDevices} />}
          {tab === "Run Single" && (
            <RunTab devices={devices} draft={runDraft} setDraft={setRunDraft} />
          )}
          {tab === "Run batch" && <ImportCasesTab devices={devices} onGoRunning={() => setTab("Running")} />}
          {tab === "Running" && <RunningTab />}
          {tab === "Re-run" && <RerunTab />}
          {tab === "Saved Tests" && <SavedTestsTab devices={devices} />}
          {tab === "History" && <HistoryTab />}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

function Setup() {
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);   // { available, version, size_bytes, released_at }

  useEffect(() => {
    let cancelled = false;
    getExeInfo()
      .then((d) => !cancelled && setInfo(d))
      .catch(() => !cancelled && setInfo({ available: false }));
    return () => { cancelled = true; };
  }, []);


  return (
    <div className="card overflow-hidden">
      <div className="border-b border-slate-100 bg-gradient-to-br from-brand-50 to-white px-5 py-4 dark:border-white/10 dark:from-brand-500/10 dark:to-ink-900">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
          <Wand2 size={16} className="text-brand-500" /> Set up an agent
        </h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          1) Ask your admin for an agent key. 2) Install the agent on the PC and paste it in.
          3) It appears online here, ready to run tests.
        </p>
      </div>
      <div className="p-5">
        {/* No "Generate key" here any more. Issuing is the admin's, on Team &
            Tokens, where they can see and revoke every machine — a tester minting
            their own meant nobody could control who was running an agent. The
            endpoint is role-guarded too; hiding the button alone would have left
            the capability in place. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadAgent().catch((e) => setErr(e.message))}>
            <Download size={15} /> Download Agent (.zip)
          </Button>
          {info && info.available && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {info.version && <>v{info.version}</>}
              {fmtBytes(info.size_bytes) && <> · {fmtBytes(info.size_bytes)}</>}
              {info.released_at && (
                <>
                  {" · "}
                  <span title={formatDateTime(info.released_at)}>
                    Updated {formatDateTime(info.released_at)}
                  </span>
                </>
              )}
            </span>
          )}
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <KeyRound size={13} className="mt-0.5 shrink-0" />
          Your admin issues the agent key — one per machine. Ask them for one, then
          paste it into the agent when it starts.
        </p>
        {err && <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300"><AlertTriangle size={14} /> {err}</p>}
      </div>
    </div>
  );
}

function SystemHealth({ devices, onPing, onRenamed }) {
  // Show online machines, plus any mid-self-update (briefly offline while swapping) so
  // the tester can see "Updating…" instead of the card vanishing.
  const shownDevices = devices.filter((d) => d.status === "online" || d.status === "updating");
  const online = devices.filter((d) => d.status === "online").length;
  return (
    <div className="space-y-4">
      <Setup />

      <div className="grid grid-cols-1 gap-3 xs:grid-cols-3">
        {[
          { label: "Agents", value: devices.length, icon: Server, tone: "text-brand-500 bg-brand-500/10" },
          { label: "Online", value: online, icon: CheckCircle2, tone: "text-emerald-500 bg-emerald-500/10" },
          { label: "Offline", value: devices.length - online, icon: CircleDot, tone: "text-slate-400 bg-slate-500/10" },
        ].map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", s.tone)}><s.icon size={16} /></span>
            <div>
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {devices.length === 0 ? (
        <EmptyState icon={Server} title="No agents yet" message="Install the .exe on a machine and connect it with a key to see it here." />
      ) : shownDevices.length === 0 ? (
        <EmptyState icon={Server} title="No agents online" message="Only online machines are shown here. Start the agent on a machine and it'll appear." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {shownDevices.map((d) => <DeviceCard key={d.id} d={d} onPing={onPing} onRenamed={onRenamed} />)}
        </div>
      )}
    </div>
  );
}

function DeviceCard({ d, onPing, onRenamed }) {
  const isUpdating = d.status === "updating";
  const isOnline = d.status === "online";
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.display_name || "");
  const [savingPersonal, setSavingPersonal] = useState(false);

  // Visibility only: this drops the machine out of other testers' pickers. A run
  // already dispatched to it keeps going, and every other device endpoint still
  // admits any same-company caller.
  async function togglePersonal(next) {
    setSavingPersonal(true);
    try {
      await setDevicePersonal(d.id, next);
      onRenamed?.();
    } catch (e) {
      toastError((e && e.message) || "Couldn't change that.");
    } finally { setSavingPersonal(false); }
  }
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);   // null | "ok" | "fail"

  const doPing = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const r = await onPing(d.id);
      setPingResult(r && r.ok ? "ok" : "fail");
    } catch (e) {
      setPingResult("fail");
    } finally {
      setPinging(false);
      onRenamed && onRenamed();                          // refresh so a stale dot flips
      setTimeout(() => setPingResult(null), 3000);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await renameExeDevice(d.id, name.trim());
      setEditing(false);
      onRenamed && onRenamed();
    } catch (e) {
      /* keep the editor open so the user can retry */
    } finally {
      setSaving(false);
    }
  };

  // system-info row (agent self-reports each heartbeat; RAM needs agent >= 0.2.0)
  const info = [
    d.lan_ip && { icon: Globe, text: d.lan_ip, title: "LAN IP" },
    d.internet_ok != null && {
      icon: d.internet_ok ? Wifi : WifiOff,
      text: d.internet_ok ? "Internet OK" : "No internet",
      title: "Internet (self-checked by the agent)",
      cls: d.internet_ok ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300",
    },
    d.disk_free_mb != null && { icon: HardDrive, text: `${fmtGb(d.disk_free_mb)} free`, title: "Disk free" },
    d.ram_total_mb != null && d.ram_free_mb != null && {
      icon: MemoryStick,
      text: `${fmtGb(d.ram_free_mb)} / ${fmtGb(d.ram_total_mb)}`,
      title: "RAM free / total",
    },
  ].filter(Boolean);

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn("relative grid h-9 w-9 shrink-0 place-items-center rounded-xl",
            isUpdating ? "bg-amber-500/10 text-amber-600 dark:text-amber-300"
              : isOnline ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
              : "bg-slate-500/10 text-slate-400")}>
            <Server size={16} />
            <span className={cn("absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-ink-900",
              isUpdating ? "bg-amber-500" : isOnline ? "bg-emerald-500" : "bg-slate-300 dark:bg-white/20")} />
          </span>
          <div className="min-w-0">
            {editing ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus value={name} onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                  placeholder="e.g. QA laptop"
                  className="w-36 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
                />
                <button type="button" disabled={saving} onClick={save} className="grid h-6 w-6 place-items-center rounded-md bg-brand-500/10 text-brand-600 hover:bg-brand-500/20 dark:text-brand-300" title="Save name">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                </button>
                <button type="button" onClick={() => setEditing(false)} className="grid h-6 w-6 place-items-center rounded-md bg-slate-500/10 text-slate-500 hover:bg-slate-500/20" title="Cancel">
                  <X size={13} />
                </button>
              </div>
            ) : (
              <p className="flex min-w-0 items-center gap-1.5 font-semibold text-slate-900 dark:text-white">
                <span className="truncate">
                  {d.display_name || d.label || d.id.slice(0, 8)}
                  {d.display_name && <span className="ml-1 font-normal text-xs text-slate-400">({d.label || d.id.slice(0, 8)})</span>}
                </span>
                <button
                  type="button"
                  onClick={() => { setName(d.display_name || ""); setEditing(true); }}
                  className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-500 dark:hover:bg-white/10"
                  title="Name this PC (e.g. who uses it)"
                >
                  <Pencil size={12} />
                </button>
              </p>
            )}
            <p className="truncate text-xs text-slate-400">
              {d.os || "—"} · v{d.agent_version || "?"}
              {d.last_updated && <span className="text-emerald-600 dark:text-emerald-400"> · updated {fmt(d.last_updated)}</span>}
            </p>
            {/* Whose machine this is. Every tester in the company can dispatch a
                run to any PC, so without this you could open a browser window on
                a colleague's desk having picked from a list of hostnames. */}
            {(d.owner_name || d.owner_email) && (
              <p className="truncate text-xs text-slate-400">
                <UserIcon size={11} className="mr-1 inline align-[-1px]" />
                {d.is_mine ? "Yours" : (d.owner_name || d.owner_email)}
                {d.is_personal && (
                  <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase text-slate-500 dark:bg-white/10 dark:text-slate-400">
                    personal
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
        {d.is_mine && (
          <label
            className="mr-2 inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
            title="Hide this machine from the rest of the team's device list. Runs already going keep running."
          >
            <input
              type="checkbox" checked={!!d.is_personal} disabled={savingPersonal}
              onChange={(e) => togglePersonal(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300"
            />
            Personal
          </label>
        )}
        <Button variant="outline" size="sm" disabled={pinging} onClick={doPing}>
          {pinging ? (
            <><Loader2 size={14} className="animate-spin" /> Pinging…</>
          ) : pingResult === "ok" ? (
            <><CheckCircle2 size={14} className="text-emerald-500" /> Responding</>
          ) : pingResult === "fail" ? (
            <><XCircle size={14} className="text-rose-500" /> No response</>
          ) : (
            "Ping"
          )}
        </Button>
      </div>
      {info.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          {info.map((i, idx) => (
            <span key={idx} title={i.title} className={cn("inline-flex items-center gap-1", i.cls)}>
              <i.icon size={12} /> {i.text}
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs dark:border-white/10">
        <span className={cn("inline-flex items-center gap-1.5 font-medium",
          isUpdating ? "text-amber-600 dark:text-amber-300"
            : d.current_run_id ? "text-brand-600 dark:text-brand-300"
            : "text-slate-500 dark:text-slate-400")}>
          {isUpdating ? <><Loader2 size={12} className="animate-spin" /> Updating…</>
            : d.current_run_id ? <><Play size={12} /> Running</>
            : <><CircleDot size={12} /> Idle</>}
        </span>
        <span className="text-slate-400">last seen {fmt(d.last_seen)}</span>
      </div>
    </div>
  );
}

// What the tester has typed but not yet run. Held by the PARENT, because this tab is
// rendered only while it is the open one: switching to "Running" and back unmounted the
// component and took the draft with it. Somebody who had written a full set of steps,
// run it, gone to look at the result and come back found an empty box — with no hint
// that anything had been discarded.
//
// Deliberately NOT persisted to localStorage. A single-run instruction routinely carries
// "username: … password: …" for the app under test, and there is no reason for that to
// outlive the tab it was typed in, let alone be written to disk.
const BLANK_RUN = { deviceId: "", url: "", instruction: "", browser: "chromium", headless: false };

function RunTab({ devices, draft, setDraft }) {
  const onlineDevices = devices.filter((d) => d.status === "online");
  const { deviceId, url, instruction, browser, headless } = draft;
  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));
  const [setDeviceId, setUrl, setInstruction, setBrowser, setHeadless] =
    [set("deviceId"), set("url"), set("instruction"), set("browser"), set("headless")];
  const [runId, setRunId] = useState(null);
  const [err, setErr] = useState("");

  const start = async () => {
    setErr("");
    try {
      const u = url.trim();
      const fullUrl = /^https?:\/\//i.test(u) ? u : `https://${u}`;
      const r = await startExeRun({ device_id: deviceId || onlineDevices[0]?.id, url: fullUrl, instruction, browser, headless });
      setRunId(r.run_id);
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 dark:border-white/10">
          <Play size={16} className="text-brand-500" />
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">New run</h3>
        </div>
        <div className="space-y-3 p-5">
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Device</span>
            <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} devices={onlineDevices} placeholder="Pick a device" />
          </div>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Website URL</span>
            <input aria-label="URL to test" placeholder="https://app-to-test.com" value={url} onChange={(e) => setUrl(e.target.value)} className={FIELD} />
          </div>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">What should we test?</span>
            <textarea placeholder='Goal, e.g. "log in with valid credentials"' value={instruction}
              onChange={(e) => setInstruction(e.target.value)} rows={3} className={cn(FIELD, "resize-none")} />
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 dark:border-white/10">
            <select value={browser} onChange={(e) => setBrowser(e.target.value)} className={cn(FIELD, "max-w-[10rem]")}>
              {BROWSERS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input type="checkbox" checked={headless} onChange={(e) => setHeadless(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" /> headless
            </label>
            <Button size="sm" className="ml-auto" disabled={!url || !instruction} onClick={start}><Play size={15} /> Run on my PC</Button>
          </div>
          {err && <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300"><AlertTriangle size={14} /> {err}</p>}
        </div>
      </div>
      {runId && <LiveRun runId={runId} />}
    </div>
  );
}

// The run's instruction is the whole composed case — "Scenario: …; Preconditions: …;
// Steps: 1. … 2. …; Test data: …; Expected: …". Printed on one truncated line, a reader
// saw the scenario name and half a precondition, and the steps the run was judged
// against were unreadable in the very panel that reports the verdict.
//
// So it is broken back into its labelled parts and shown in full, collapsed to the
// first two lines until asked for. Nothing is hidden that a failure would need.
const INSTRUCTION_PARTS = /(Scenario|Preconditions|Steps|Test data|Expected|Verify text)\s*:/gi;

function splitInstruction(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const marks = [...raw.matchAll(INSTRUCTION_PARTS)];
  if (!marks.length) return [{ label: "", body: raw }];
  return marks.map((m, i) => ({
    label: m[1],
    body: raw.slice(m.index + m[0].length, i + 1 < marks.length ? marks[i + 1].index : undefined)
      .replace(/^\s*/, "").replace(/;\s*$/, "").trim(),
  })).filter((p) => p.body);
}

function RunInstruction({ text }) {
  const [open, setOpen] = useState(false);
  const parts = splitInstruction(text);
  if (!parts.length) return null;
  return (
    <div className="min-w-0 flex-1">
      <button type="button" onClick={() => setOpen((v) => !v)}
        title={open ? "Show less" : "Show the full case"}
        className="flex w-full items-start gap-1.5 text-left">
        <div className={cn("min-w-0 flex-1 space-y-0.5", !open && "line-clamp-2")}>
          {parts.map((p, i) => (
            <p key={i} className="whitespace-pre-wrap break-words text-sm text-slate-800 dark:text-white">
              {p.label && <span className="mr-1.5 font-semibold text-slate-500 dark:text-slate-400">{p.label}:</span>}
              <span className={i === 0 ? "font-medium" : ""}>{p.body}</span>
            </p>
          ))}
        </div>
        <ChevronDown size={15} aria-hidden
          className={cn("mt-0.5 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
    </div>
  );
}

function LiveRun({ runId }) {
  const [run, setRun] = useState(null);
  const [shotStep, setShotStep] = useState(null);   // step index whose screenshot is open in the lightbox
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await getExeRun(runId);
        if (!stop) setRun(r);
        if (!stop && !isTerminalExe(r.status)) setTimeout(poll, 1000);
      } catch (e) {
        if (!stop) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [runId]);

  if (!run) {
    return (
      <div className="card flex items-center gap-2 p-5 text-sm text-slate-500 dark:text-slate-400">
        <Loader2 size={16} className="animate-spin text-brand-500" /> Starting…
      </div>
    );
  }
  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 px-5 py-4 dark:border-white/10">
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          <ExeStatus status={run.status} />
          <RunInstruction text={run.instruction} />
        </div>
        <div className="flex flex-wrap gap-2">
          {!isTerminalExe(run.status) && <Button variant="outline" size="sm" onClick={() => cancelExeRun(runId).catch((e) => toastError(e?.message || "Couldn't stop the run."))}><Square size={14} /> Stop</Button>}
          {isTerminalExe(run.status) && <Button variant="outline" size="sm" onClick={() => compileExeRun(runId, "").then(() => toastSuccess("Saved as a reusable test.")).catch((e) => toastError(e?.message || "Couldn't save this run as a test."))}><Save size={14} /> Save as test</Button>}
          {run.status === "failed" && <Button variant="outline" size="sm" onClick={() => rootCauseExeRun(runId).catch((e) => toastError(e?.message || "Couldn't run root-cause analysis."))}><Search size={14} /> Root cause</Button>}
        </div>
      </div>
      {isTerminalExe(run.status) && (run.duration_seconds != null || run.token_usage) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-slate-100 px-5 py-2.5 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400">
          <span>⏱ {fmtDur(run.duration_seconds)}</span>
          {run.is_replay ? (
            <span className="font-medium text-emerald-600 dark:text-emerald-400">⚡ replay — no AI cost</span>
          ) : (
            <>
              {run.token_usage && run.token_usage.model && <span>🧠 {run.token_usage.model}</span>}
              {fmtTokens(run.token_usage) && <span>{fmtTokens(run.token_usage)}</span>}
              {fmtCost(run.token_usage) && <span>💲 {fmtCost(run.token_usage)} est.</span>}
            </>
          )}
        </div>
      )}
      <div className="p-5">
        <ol className="space-y-2">
          {(run.steps || []).map((s, i) => {
            const failed = s.success === false;
            // Screenshots are captured ONLY on failed steps + the final state, so show the
            // "View" button ONLY where one actually exists (has_shot), with a failed-step
            // fallback for older runs saved before the flag existed.
            const hasShot = s.has_shot || failed;
            return (
              <li key={i} className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 dark:border-white/5 dark:bg-white/5">
                <span className={cn("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-white", failed ? "bg-rose-500" : "bg-emerald-500")}>
                  {failed ? <XCircle size={12} /> : <Check size={12} strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1 text-sm text-slate-700 dark:text-slate-200">
                  <span className="font-medium capitalize">{s.action}</span>{" "}
                  <span className="text-slate-400">{s.target || s.selector || ""}</span>
                  {s.detail && <span className="block text-xs text-slate-500 dark:text-slate-400">{s.detail}</span>}
                </span>
                {hasShot && (
                  <button type="button" onClick={() => setShotStep(i)}
                    className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-500/10 dark:text-brand-300">
                    <Camera size={13} /> View
                  </button>
                )}
              </li>
            );
          })}
        </ol>
        {run.root_cause && run.root_cause.summary && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            <Search size={15} className="mt-0.5 shrink-0" /> {run.root_cause.summary}
          </div>
        )}
      </div>
      {shotStep !== null && <ShotModal runId={runId} step={shotStep} onClose={() => setShotStep(null)} />}
    </div>
  );
}

// Full-screen screenshot lightbox (mirrors the Extension tab): click the backdrop or ✕ to close.
function ShotModal({ runId, step, onClose }) {
  const [src, setSrc] = useState(undefined); // undefined = loading, null = none, string = url
  useEffect(() => {
    let dead = false;
    setSrc(undefined);
    authedBlobUrl(exeScreenshotPath(runId, step)).then((u) => !dead && setSrc(u));
    return () => { dead = true; };
  }, [runId, step]);
  return (
    <div onClick={onClose} className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/85 p-6">
      <button type="button" aria-label="Close" onClick={onClose}
        className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-white transition-colors hover:bg-white/20">
        <X size={20} />
      </button>
      {src === undefined ? (
        <div className="flex items-center gap-2 text-sm text-white/90"><Loader2 size={18} className="animate-spin" /> Loading screenshot…</div>
      ) : !src ? (
        <div className="rounded-xl bg-white/10 px-4 py-3 text-sm text-white">No screenshot for this step.</div>
      ) : (
        <img src={src} alt={`step ${step + 1}`} onClick={(e) => e.stopPropagation()}
          className="max-h-[85vh] max-w-full rounded-xl shadow-glow" />
      )}
    </div>
  );
}

function SavedTestsTab({ devices }) {
  const onlineDevices = devices.filter((d) => d.status === "online");
  const [tests, setTests] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const reload = () => listExeTests().then(setTests).catch(() => {});
  useEffect(() => { reload(); }, []);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="sm:max-w-xs">
          <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} devices={onlineDevices} placeholder="Replay on…" />
        </div>
        <DeleteAllButton
          disabled={!tests.length}
          label="Delete all"
          title="Delete all saved tests?"
          message={`Permanently delete all ${tests.length} saved test${tests.length === 1 ? "" : "s"} for your team.`}
          onDelete={deleteAllExeTests}
          onDone={reload}
        />
      </div>
      {tests.length === 0 ? (
        <EmptyState icon={Bookmark} title="No saved tests yet" message="Run something on the Run tab, then “Save as test” to replay it any time." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {tests.map((t) => (
            <div key={t.id} className="card flex items-center justify-between gap-3 p-4">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Bookmark size={16} /></span>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900 dark:text-white">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.steps} steps</p>
                </div>
              </div>
              <Button size="sm" disabled={!deviceId} onClick={() => replayExeTest(t.id, deviceId)}><Repeat size={14} /> Replay</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared status → colour map for the batch/case pills (History tab, and any other
// batch list that shows per-status counts).
const BATCH_STATUS_TONE = {
  passed: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300",
  failed: "text-rose-600 bg-rose-500/10 dark:text-rose-300",
  error: "text-amber-600 bg-amber-500/10 dark:text-amber-300",
  cancelled: "text-slate-500 bg-slate-500/10 dark:text-slate-400",
};


// History — persistent, batch-first explorer (mirrors the Extension History
// tab). Reads exe BATCHES (which survive deleting imported cases + run history
// elsewhere), so past results stay browsable. Search + status filter +
// newest/oldest sort; expand a batch to its cases; open a case for its steps
// and screenshots. No destructive "delete all" — it's the permanent record.
function HistoryTab() {
  const [batches, setBatches] = useState([]);
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [items, setItems] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [detailRun, setDetailRun] = useState(null);   // {runId, name} → step/screenshot modal
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");

  const reload = useCallback(() => {
    setLoading(true);
    // Two sources: batches, plus the Run Single runs that no batch item owns.
    // This tab used to read batches alone, so a single run left no trace in
    // History at all. Settled independently — one failing must not blank the other.
    Promise.allSettled([listExeBatches(50), listExeRuns(50, { standalone: true })])
      .then(([b, r]) => {
        if (b.status === "fulfilled") setBatches(Array.isArray(b.value) ? b.value : (b.value?.batches || []));
        else toastError(b.reason?.message || "Couldn't load batch history.");
        if (r.status === "fulfilled") setSingles(Array.isArray(r.value) ? r.value : (r.value?.items || []));
        else toastError(r.reason?.message || "Couldn't load single-run history.");
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!items[id]) {
      setBusyId(id);
      try {
        const it = await listExeBatchItems(id);
        setItems((s) => ({ ...s, [id]: Array.isArray(it) ? it : (it?.items || []) }));
      } catch (e) {
        toastError(e?.message || "Couldn't load the cases for this run.");
      } finally { setBusyId(null); }
    }
  };

  const statusOptions = useMemo(
    () => Array.from(new Set(
      [...batches, ...singles].map((e) => String(e.status || "").toLowerCase()).filter(Boolean)
    )),
    [batches, singles]
  );
  // Batches and single runs interleaved by time, so a single run appears where it
  // ran instead of in a separate section below the fold.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const has = (text) => !q || String(text || "").toLowerCase().includes(q);
    const entries = [
      ...batches.map((b) => ({ kind: "batch", key: `b-${b.id}`, at: b.created_at, data: b })),
      ...singles.map((r) => ({ kind: "run", key: `r-${r.id}`, at: r.created_at, data: r })),
    ];
    return entries
      .filter((e) => {
        if (status !== "all" && String(e.data.status || "").toLowerCase() !== status) return false;
        if (e.kind === "batch") {
          return has(e.data.name || `Batch ${String(e.data.id).slice(0, 8)}`) || has(e.data.id);
        }
        return has(e.data.instruction) || has(e.data.url) || has(e.data.id);
      })
      .sort((a, b) => {
        const da = new Date(a.at || 0).getTime();
        const db = new Date(b.at || 0).getTime();
        return sort === "newest" ? db - da : da - db;
      });
  }, [batches, singles, query, status, sort]);

  const selectCls = "h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Search batches and runs" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search history…"
            className="h-9 w-52 rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            <option value="all">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
      ) : visible.length === 0 ? (
        <EmptyState icon={History} title={batches.length + singles.length ? "Nothing matches" : "No runs yet"}
          message={batches.length + singles.length ? "Try a different search or status filter." : "Run a single test or a batch and it shows up here permanently — with each case's steps and screenshots."} />
      ) : (
        <div className="space-y-3">
          {visible.map((entry) => {
            // A single run has no cases to expand — the card opens its own detail.
            if (entry.kind === "run") {
              const r = entry.data;
              return (
                <button key={entry.key} type="button"
                  onClick={() => setDetailRun({ runId: r.id, name: r.instruction || r.url || "Single run" })}
                  className="card flex w-full items-center gap-3 p-4 text-left transition-colors hover:border-brand-300 dark:hover:border-brand-500/40">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"><Play size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-semibold text-slate-900 dark:text-white">{r.instruction || r.url || `Run ${String(r.id).slice(0, 8)}`}</span>
                      <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">single run</span>
                      {r.is_replay && <span title="Replay of an earlier run" className="shrink-0 text-slate-400">⚡</span>}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {fmt(r.created_at)}
                      {r.duration_seconds != null ? ` · ${Number(r.duration_seconds).toFixed(1)}s` : ""}
                      {r.browser ? ` · ${r.browser}` : ""}
                      {r.url ? ` · ${r.url}` : ""}
                    </span>
                  </span>
                  <ExeStatus status={r.status} />
                  <span className="shrink-0 text-slate-400"><ChevronRight size={16} /></span>
                </button>
              );
            }
            const b = entry.data;
            const open = openId === b.id;
            const rows = items[b.id] || [];
            return (
              <div key={entry.key} className="card overflow-hidden">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" onClick={() => toggle(b.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><History size={16} /></span>
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-slate-900 dark:text-white">{b.name || `Batch ${String(b.id).slice(0, 8)}`}</span>
                      <span className="block text-xs text-slate-400">{fmt(b.created_at)} · {b.total} case{b.total === 1 ? "" : "s"} · {b.status}</span>
                    </span>
                    <span className={cn("ml-auto shrink-0 text-slate-400 transition-transform", open && "rotate-90")}><ChevronRight size={16} /></span>
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    {[["passed", b.passed], ["failed", b.failed], ["error", b.errored]].map(([k, v]) => (
                      <span key={k} className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", BATCH_STATUS_TONE[k])}>{v || 0} {k}</span>
                    ))}
                    <Button variant="outline" size="sm"
                      onClick={() => downloadExeBatchReport(b.id).catch((e) => toastError(e?.message || "Couldn't download the report."))}>
                      <Download size={13} /> Report
                    </Button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-slate-100 px-4 py-3 dark:border-white/10">
                    {busyId === b.id ? (
                      <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Loading cases…</div>
                    ) : rows.length === 0 ? (
                      <p className="py-3 text-sm text-slate-400">No cases recorded for this run.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="text-slate-400">
                            <tr>
                              <th className="py-1.5 pr-3 font-medium">#</th>
                              <th className="py-1.5 pr-3 font-medium">TC ID</th>
                              <th className="py-1.5 pr-3 font-medium">Test case</th>
                              <th className="py-1.5 pr-3 font-medium">Status</th>
                              <th className="py-1.5 pr-3 font-medium">Device</th>
                              <th className="py-1.5 pr-3 font-medium">Time</th>
                              <th className="py-1.5 text-right font-medium">Detail</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {rows.map((it, idx) => {
                              const clickable = !!it.run_id;
                              return (
                                <tr key={it.id}
                                  onClick={() => clickable && setDetailRun({ runId: it.run_id, name: it.name })}
                                  className={clickable ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5" : ""}>
                                  <td className="py-1.5 pr-3 text-slate-400">{(it.order_index ?? idx) + 1}</td>
                                  <td className="py-1.5 pr-3 text-slate-500 dark:text-slate-400">{it.external_id || "—"}</td>
                                  <td className="py-1.5 pr-3 text-slate-700 dark:text-slate-200">
                                    {it.name}{it.is_replay ? " ⚡" : ""}
                                    {it.deleted_case && <span className="ml-1.5 text-[0.625rem] text-slate-400" title="The imported case was deleted; recovered from the run">(case deleted)</span>}
                                  </td>
                                  <td className="py-1.5 pr-3"><ExeStatus status={it.status} /></td>
                                  <td className="py-1.5 pr-3 text-slate-500 dark:text-slate-400">{it.device || "—"}</td>
                                  <td className="py-1.5 pr-3 text-slate-400">{it.duration_seconds ? `${Number(it.duration_seconds).toFixed(1)}s` : "—"}</td>
                                  <td className="py-1.5 text-right">
                                    {clickable
                                      ? <span className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-300">View <ChevronRight size={12} /></span>
                                      : <span className="text-slate-300 dark:text-slate-600">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {detailRun && (
        <Modal open onClose={() => setDetailRun(null)} title={detailRun.name || "Run detail"} size="xl">
          <LiveRun runId={detailRun.runId} />
        </Modal>
      )}
    </div>
  );
}

// =========================================================================== //
//  Import Cases + batch distribution across many PCs
// =========================================================================== //
const MAP_FIELDS = [
  { key: "name", label: "Test name" },
  { key: "url", label: "Page URL" },
  { key: "instruction", label: "Steps / what to do", required: true },
  { key: "expected_result", label: "Expected result" },
  { key: "test_scenario", label: "Test scenario" },
  { key: "preconditions", label: "Preconditions" },
  { key: "test_data", label: "Test data" },
  { key: "external_id", label: "Test case ID" },
  { key: "category", label: "Module / feature area" },
  { key: "test_type", label: "Test type (UI / functional / API)" },
  { key: "run_mode", label: "Run mode (auto / manual_only)" },
  { key: "manual_reason", label: "Manual reason (why a human must run it)" },
  { key: "verify_text", label: "Verify text (exact on-page string that proves a pass)" },
  { key: "destructive", label: "Destructive (YES = never auto-run)" },
];

const ALIASES = {
  external_id: ["test case id", "test id", "tc id", "tcid", "id", "sr", "sr.", "sr no", "s.no", "#"],
  name: ["test case", "test name", "name", "title", "scenario name", "case name"],
  url: ["url", "link", "access link", "page url", "website", "app link", "site", "page"],
  instruction: ["test case steps", "test steps", "steps", "steps to do", "action", "actions", "procedure", "how to test"],
  // Matched BEFORE expected_result and test_data: their loose "expected" / "data"
  // aliases would otherwise swallow an "Expected Text" or "Data Changing" column on
  // the substring pass, and the run would lose the only string that proves a pass.
  verify_text: ["verify text", "verify_text", "verification text", "assert text", "assertion", "expected text", "verify"],
  destructive: ["destructive", "is destructive", "data changing", "mutates data"],
  expected_result: ["expected result", "expected outcome", "expected", "result"],
  test_scenario: ["test scenario", "scenario"],
  test_description: ["test description", "description", "desc"],
  preconditions: ["preconditions", "precondition", "pre-condition", "pre condition"],
  test_data: ["test data", "data", "input data"],
  // Module and Test Type are DIFFERENT columns ("Payments" vs "UI") and sheets carry
  // both — keep "type" out of category, or whichever column appears first wins and the
  // other is silently dropped at import.
  category: ["module", "category", "feature", "area", "component"],
  run_mode: ["run mode", "run_mode", "runmode", "mode", "execution", "execution type", "automation"],
  manual_reason: ["manual reason", "manual_reason", "why manual", "manual note", "manual comment"],
  // Matched LAST: its loose "type" alias would otherwise swallow a sheet's
  // "Execution Type" column, which belongs to run_mode.
  test_type: ["test type", "test_type", "testtype", "type", "kind", "test kind"],
};

function autoGuess(columns) {
  const m = {};
  const norm = (s) => String(s || "").trim().toLowerCase();
  const used = new Set();
  for (const field of Object.keys(ALIASES)) {
    let pick = "";
    for (const col of columns) {
      if (!used.has(col) && ALIASES[field].includes(norm(col))) { pick = col; break; }
    }
    if (!pick)
      for (const col of columns) {
        if (used.has(col)) continue;
        const n = norm(col);
        if (ALIASES[field].some((a) => n.includes(a))) { pick = col; break; }
      }
    if (pick) { m[field] = pick; used.add(pick); }
  }
  return m;
}

const cellStr = (row, col) => (col && row[col] != null ? String(row[col]).trim() : "");

// Placeholder values that show up in a "URL" column but aren't real links. Mirrors
// the backend's _clean_url so a column of "N/A" doesn't count as a website (which
// otherwise hides the "enter one URL" box and makes runs open https://N/A).
const URL_PLACEHOLDERS = new Set([
  "", "n/a", "n/a.", "na", "n.a", "n.a.", "n\\a", "-", "--", "---", "—",
  "none", "null", "nil", "tbd", "todo", "n / a", ".",
]);
const cleanUrl = (v) => {
  const u = String(v ?? "").trim();
  const low = u.toLowerCase();
  // A real URL never contains a space — reject prose like "URL: https://x.com"
  // or "see homepage" so those rows fall back to the typed default URL.
  if (!u || u.includes(" ")) return "";
  if (URL_PLACEHOLDERS.has(low)) return "";
  if (low.startsWith("http://") || low.startsWith("https://") || u.includes(".") || low.split(":")[0] === "localhost") return u;
  return "";
};

// A cell counts as a real link only if the whole value cleans to itself (a bare
// URL/host, no label or prose around it).
const isUrlCell = (v) => { const u = String(v ?? "").trim(); return !!u && cleanUrl(u) === u; };

// Is `col` actually a website-link column, or did auto-detect just latch onto a
// column that happens to mention a URL once (e.g. Test Data)? True only when a
// real majority of its non-empty cells are bare links.
const looksLikeUrlColumn = (rows, col) => {
  if (!col) return false;
  let nonEmpty = 0, urlish = 0;
  for (const r of rows) { const v = cellStr(r, col); if (!v) continue; nonEmpty += 1; if (isUrlCell(v)) urlish += 1; }
  return nonEmpty > 0 && urlish / nonEmpty >= 0.5;
};

// Drop a Page-URL mapping that isn't really a link column, so the "enter one
// website URL" box appears (and the user sets one URL for all) instead of the
// importer silently skipping every row for "no URL".
const withValidatedUrl = (m, rows) => {
  if (m.url && !looksLikeUrlColumn(rows, m.url)) { const next = { ...m }; delete next.url; return next; }
  return m;
};

function ImportCasesTab({ devices, onGoRunning }) {
  const onlineDevices = devices.filter((d) => d.status === "online");
  const [cases, setCases] = useState([]);
  const [imported, setImported] = useState(0);  // how many the server holds, vs cases.length loaded
  const [parsed, setParsed] = useState(null);   // {wb, fileName, sheetNames, sheet, columns, rows}
  const [mapping, setMapping] = useState({});
  const [defaultUrl, setDefaultUrl] = useState("");
  // Which dashboard Project this sheet belongs to. Batches inherit it from the
  // cases and runs reach a project only through their batch, so a sheet imported
  // without it produces work no per-project figure can attribute.
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  // "Move to project" for cases imported before a project was required. Their
  // runs can never reach a project's totals until they are attached.
  const [moveTo, setMoveTo] = useState("");
  const [moving, setMoving] = useState(false);
  useEffect(() => {
    getProjects().then((p) => setProjects(Array.isArray(p) ? p : [])).catch(() => setProjects([]));
  }, []);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [detected, setDetected] = useState(false); // has AI auto-detect run for the current file/sheet?
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [showRun, setShowRun] = useState(false);
  const [fixingLogins, setFixingLogins] = useState(false);
  const [showRan, setShowRan] = useState(false);
  const fileRef = useRef(null);

  // Loads EVERY page — one capped request left 371 of 871 cases invisible. `imported`
  // is what the server says exists, so a short load is stated instead of hidden.
  const reload = useCallback(() => {
    loadAllExeTestCases()
      .then(({ items, total }) => { setCases(items); setImported(total); })
      .catch((e2) => toastError(e2?.message || "Could not load the imported cases."));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const pending = useMemo(() => cases.filter((c) => !c.ran), [cases]);
  const ranCount = cases.length - pending.length;
  // Cases with no project. These are exactly the ones whose runs can never
  // reach a project's totals, which is what made every project read 0 runs.
  const orphanCases = useMemo(() => cases.filter((c) => !c.project_id), [cases]);
  const visibleCases = showRan ? cases : pending;

  const readSheet = (wb, name) => {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { defval: "" });
    return { rows, columns: rows.length ? Object.keys(rows[0]) : [] };
  };

  const onFile = async (e) => {
    setErr(""); setMsg("");
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      let best = wb.SheetNames[0];
      let bestScore = -1;
      for (const sn of wb.SheetNames) {
        const score = Object.keys(autoGuess(readSheet(wb, sn).columns)).length;
        if (score > bestScore) { bestScore = score; best = sn; }
      }
      const { rows, columns } = readSheet(wb, best);
      setParsed({ wb, fileName: file.name, sheetNames: wb.SheetNames, sheet: best, columns, rows });
      setMapping(withValidatedUrl(autoGuess(columns), rows));
      setDefaultUrl("");
      setDetected(false); // new file → require a fresh detect pass
    } catch (e2) {
      setErr("Could not read that file. Use a .xlsx or .xls spreadsheet.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pickSheet = (sn) => {
    const { rows, columns } = readSheet(parsed.wb, sn);
    setParsed({ ...parsed, sheet: sn, columns, rows });
    setMapping(withValidatedUrl(autoGuess(columns), rows));
    setDetected(false); // switched sheet → require a fresh detect pass
  };

  const aiDetect = async () => {
    if (!parsed) return;
    setAiBusy(true); setErr("");
    try {
      const res = await detectExeColumns(parsed.columns, parsed.rows.slice(0, 3));
      // The backend answers 200 + {error} when the AI PROVIDER failed (no
      // credits / rejected key / rate limit). Show that reason — collapsing it
      // into "couldn't map it" makes a misconfigured provider look like a bad
      // guess, and nobody can tell which one they're looking at.
      if (res?.error) setErr(res.error);
      else if (res && Object.keys(res).length) setMapping((m) => withValidatedUrl({ ...m, ...res }, parsed.rows));
      else setErr("AI couldn’t match any column — please map them manually.");
    } catch (e2) {
      setErr(e2?.message || "AI detect failed — map the columns manually.");
    } finally {
      setAiBusy(false);
      setDetected(true); // detect attempted → unlock import (even if it fell back to manual)
    }
  };

  const preview = useMemo(() => {
    if (!parsed) return { ready: 0, skipped: 0 };
    let ready = 0, skipped = 0;
    for (const r of parsed.rows) {
      const steps = cellStr(r, mapping.instruction) || cellStr(r, mapping.test_description);
      const url = cleanUrl(cellStr(r, mapping.url)) || defaultUrl.trim();
      if (steps && url) ready++; else skipped++;
    }
    return { ready, skipped };
  }, [parsed, mapping, defaultUrl]);

  // Show the "enter one URL" box when no mapped column yields a real link — this
  // covers both "no URL column" and "a column that's all N/A" (which auto-detect
  // may otherwise map, hiding the box and breaking the runs).
  const noUrlColumn = parsed && !parsed.rows.some((r) => cleanUrl(cellStr(r, mapping.url)));

  const doImport = async () => {
    if (!parsed) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const payloadCases = parsed.rows.map((r) => ({
        external_id: cellStr(r, mapping.external_id),
        name: cellStr(r, mapping.name),
        url: cleanUrl(cellStr(r, mapping.url)),
        instruction: cellStr(r, mapping.instruction),
        expected_result: cellStr(r, mapping.expected_result),
        test_scenario: cellStr(r, mapping.test_scenario),
        test_description: cellStr(r, mapping.test_description),
        preconditions: cellStr(r, mapping.preconditions),
        test_data: cellStr(r, mapping.test_data),
        category: cellStr(r, mapping.category),
        run_mode: cellStr(r, mapping.run_mode),
        manual_reason: cellStr(r, mapping.manual_reason),
        verify_text: cellStr(r, mapping.verify_text),
        destructive: cellStr(r, mapping.destructive),
      }));
      const res = await importExeTestCases({
        cases: payloadCases, imported_from: parsed.fileName,
        default_url: defaultUrl.trim(), project_id: projectId || null,
      });
      const bits = [`Imported ${res.imported_count} case${res.imported_count === 1 ? "" : "s"}`];
      if (res.duplicates) bits.push(`${res.duplicates} duplicate${res.duplicates === 1 ? "" : "s"} skipped`);
      if (res.skipped) bits.push(`${res.skipped} skipped (no steps/URL)`);
      setMsg(bits.join(" · ") + ".");
      setParsed(null); setMapping({}); setDefaultUrl(""); setDetected(false);
      reload();
    } catch (e2) {
      setErr(e2.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4 dark:border-white/10">
          <FileSpreadsheet size={16} className="text-brand-500" />
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Import test cases</h3>
        </div>
        <div className="p-5">
          {!parsed ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Upload an Excel sheet. Any column names work — you map them in the next step.
              </p>
              <input ref={fileRef} type="file" accept=".xls,.xlsx" onChange={onFile} className="hidden" />
              <Button size="sm" onClick={() => fileRef.current?.click()}><Upload size={15} /> Choose .xlsx file</Button>
              {msg && <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-300"><Check size={14} /> {msg}</p>}
              {err && <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300"><AlertTriangle size={14} /> {err}</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Sheet:</span>
                  {parsed.sheetNames.length > 1 ? (
                    <select value={parsed.sheet} onChange={(e) => pickSheet(e.target.value)} className={cn(FIELD, "max-w-[12rem]")}>
                      {parsed.sheetNames.map((sn) => <option key={sn} value={sn}>{sn}</option>)}
                    </select>
                  ) : <span className="font-medium text-slate-800 dark:text-white">{parsed.sheet}</span>}
                  <span className="text-slate-400">· {parsed.rows.length} rows</span>
                </div>
                <Button variant="outline" size="sm" onClick={aiDetect} disabled={aiBusy}>
                  {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Auto-detect with AI
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {MAP_FIELDS.map((f) => {
                  const sample = parsed.rows[0] ? cellStr(parsed.rows[0], mapping[f.key]) : "";
                  return (
                    <div key={f.key}>
                      <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                        {f.label}{f.required && <span className="text-rose-500"> *</span>}
                      </span>
                      <select value={mapping[f.key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} className={FIELD}>
                        <option value="">— none —</option>
                        {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {sample && <p className="mt-1 truncate text-[0.6875rem] text-slate-400">e.g. {sample}</p>}
                    </div>
                  );
                })}
              </div>

              {noUrlColumn && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-500/20 dark:bg-amber-500/10">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">No website link column found.</p>
                  <p className="mt-0.5 text-xs text-amber-700/90 dark:text-amber-300/90">Enter one website URL to use for all these cases:</p>
                  <input aria-label="Default URL for this batch" value={defaultUrl} onChange={(e) => setDefaultUrl(e.target.value)} placeholder="https://app-to-test.com" className={cn(FIELD, "mt-2")} />
                </div>
              )}

              <div>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Project</span>
                  <select aria-label="Project for these cases" value={projectId}
                          onChange={(e) => setProjectId(e.target.value)} className={FIELD}>
                    <option value="">— choose a project —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <p className="mt-1.5 text-xs text-slate-400">
                  {projectId
                    ? "Runs from these cases will count towards this project's test cases, runs and bugs."
                    : "Pick the project these cases belong to. Without it their runs can never appear in any project's totals or reports."}
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 dark:border-white/10">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-semibold text-emerald-600 dark:text-emerald-300">{preview.ready} ready</span>
                  {preview.skipped > 0 && <> · {preview.skipped} skipped (no steps/URL)</>}
                  {!detected && (
                    <span className="ml-2 inline-flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
                      <Sparkles size={12} /> Run <b>Auto-detect with AI</b> first to enable import.
                    </span>
                  )}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setParsed(null); setMapping({}); setDetected(false); }}>Cancel</Button>
                  {/* A project is required: it is the only link that lets these
                      cases' runs ever reach a project's totals. See the note on the
                      selector above. */}
                  <Button size="sm"
                    disabled={busy || !detected || preview.ready === 0 || (noUrlColumn && !defaultUrl.trim())
                      || (projects.length > 0 && !projectId)}
                    onClick={doImport}
                    title={!detected ? "Run Auto-detect with AI first"
                      : (projects.length > 0 && !projectId) ? "Choose the project these cases belong to" : undefined}>
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Import {preview.ready}
                  </Button>
                </div>
              </div>
              {/* items-start + shrink-0: a provider reason ("no credits left — top
                  it up…") wraps to two lines, which squashed a centred icon. */}
              {err && <p className="flex items-start gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300"><AlertTriangle size={14} className="mt-px shrink-0" /> {err}</p>}
            </div>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-4 dark:border-white/10">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
            <ListChecks size={16} className="text-brand-500" /> {showRan ? "All cases" : "Cases to run"} <span className="text-slate-400">({visibleCases.length})</span>
            {/* Loaded vs imported, always on show: the list used to stop at the API's
                page cap with nothing on screen to say rows were missing. */}
            <span
              className={cn("text-xs font-medium", cases.length < imported ? "text-amber-600 dark:text-amber-400" : "text-slate-400")}
              title={cases.length < imported ? "Some imported cases could not be loaded — press Refresh." : undefined}>
              {cases.length} of {imported} loaded
            </span>
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {ranCount > 0 && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <input type="checkbox" checked={showRan} onChange={(e) => setShowRan(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                Show already-run ({ranCount})
              </label>
            )}
            <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={14} /> Refresh</Button>
            {/* Only offered while something is actually unattributed — once every
                case belongs to a project this control has nothing to do and
                disappears rather than sitting there inert. */}
            {orphanCases.length > 0 && projects.length > 0 && (
              <div className="flex items-center gap-1.5">
                <select
                  aria-label="Move unattributed cases to a project"
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                  className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                >
                  <option value="">Move {orphanCases.length} unassigned to…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!moveTo || moving}
                  title="Attach these cases to a project so their future runs count towards it"
                  onClick={async () => {
                    setMoving(true);
                    try {
                      const res = await moveExeTestCases(orphanCases.map((c) => c.id), moveTo);
                      const name = projects.find((p) => p.id === moveTo)?.name || "the project";
                      // Deliberately explicit about the limit: attaching the CASE
                      // does not retro-fit batches that already ran without one.
                      toastSuccess(`${res?.moved ?? 0} case(s) moved to ${name}. Runs from here on count towards it; past runs stay unattributed.`);
                      setMoveTo("");
                      await reload();
                    } catch (e) {
                      toastError((e && e.message) || "Could not move those cases.");
                    } finally {
                      setMoving(false);
                    }
                  }}
                >
                  {moving ? <Loader2 size={14} className="animate-spin" /> : <FolderKanban size={14} />} Move
                </Button>
              </div>
            )}
            {/* A case pushed before the login became a NAME carries the password as it
                was that day. When the app's test account is rotated those cases fail on
                the sign-in screen and the sheet reads as though the app broke — this
                converts them in place, keeping their ids and their run history. */}
            <Button
              variant="outline"
              size="sm"
              disabled={!cases.length || fixingLogins}
              title="Make every case sign in with the project's current password"
              onClick={async () => {
                setFixingLogins(true);
                try {
                  let out = await refreshExeLogins();
                  // Cases imported from a downloaded sheet carry a login but no project,
                  // so nothing can fill the placeholder for them. Ask which project they
                  // belong to rather than leaving them half-converted.
                  if (out.unlinked > 0) {
                    const list = await getProjects();
                    const rows = Array.isArray(list) ? list : list?.items || [];
                    const choice = window.prompt(
                      `${out.unlinked} case(s) were imported from a sheet and are not `
                      + `linked to a project, so there is nowhere to read the current `
                      + `password from.\n\nType the number of the project they belong `
                      + `to:\n`
                      + rows.map((p, i) => `${i + 1}. ${p.name}`).join("\n"),
                    );
                    const picked = rows[Number(choice) - 1];
                    if (picked) out = await refreshExeLogins(picked.id);
                  }
                  toastSuccess(out.message);
                  await reload();
                } catch (e) {
                  toastError(e);
                } finally {
                  setFixingLogins(false);
                }
              }}
            >
              <KeyRound size={14} /> {fixingLogins ? "Updating…" : "Fix logins"}
            </Button>
            <DeleteAllButton
              disabled={!cases.length}
              title="Delete all imported cases?"
              message={`Permanently delete all ${cases.length} imported case${cases.length === 1 ? "" : "s"} for your team. Past run history is kept.`}
              onDelete={deleteAllExeTestCases}
              onDone={reload}
            />
            <Button size="sm" disabled={visibleCases.length === 0} onClick={() => setShowRun(true)}><Rocket size={15} /> Run test cases</Button>
          </div>
        </div>
        {cases.length === 0 ? (
          <EmptyState icon={FileSpreadsheet} title="No imported cases yet" message="Upload an .xlsx above to add test cases here." />
        ) : visibleCases.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="All cases have been run" message="Every imported case has finished a batch. Tick “Show already-run” to see them or view results in the Running tab." />
        ) : (
          <ul className="max-h-[28rem] divide-y divide-slate-100 overflow-auto dark:divide-white/5">
            {visibleCases.map((c) => {
              const ranOk = c.last_status === "passed";
              return (
                <li key={c.id} className={cn("flex items-center gap-3 px-5 py-3 text-sm", c.ran && "opacity-70")}>
                  <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-lg", c.replayable ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "bg-brand-500/10 text-brand-600 dark:text-brand-300")}>
                    {c.replayable ? <Repeat size={14} /> : <FileSpreadsheet size={14} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-800 dark:text-white">{c.name}</p>
                    <p className="truncate text-xs text-slate-400">{c.url}{c.replayable ? " · ⚡ replay-ready" : ""}</p>
                  </div>
                  {c.ran && (
                    <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold",
                      ranOk ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "bg-rose-500/10 text-rose-600 dark:text-rose-300")}>
                      {ranOk ? "passed" : (c.last_status || "ran")}
                    </span>
                  )}
                  <button type="button" onClick={() => deleteExeTestCase(c.id).then(reload).catch(() => {})} className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-500/10 hover:text-rose-500" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showRun && (
        <RunTestCasesModal
          cases={visibleCases}
          onlineDevices={onlineDevices}
          onClose={() => setShowRun(false)}
          onStarted={(r) => {
            setShowRun(false);
            // The server reports what it actually did with the selection — how many
            // cases it parked as manual-only and how many chosen PCs had gone offline.
            // Say it out loud instead of dropping it on the floor.
            const notes = [];
            if (r?.manual_only) notes.push(`${r.manual_only} manual-only (parked as blocked)`);
            if (r?.skipped_offline) notes.push(`${r.skipped_offline} PC${r.skipped_offline === 1 ? "" : "s"} skipped — offline`);
            toastSuccess(
              `${r?.total ?? 0} case${r?.total === 1 ? "" : "s"} queued on ${r?.online_devices ?? 0} PC${r?.online_devices === 1 ? "" : "s"}`
              + (notes.length ? ` · ${notes.join(" · ")}` : "")
            );
            onGoRunning();
          }}
        />
      )}
    </div>
  );
}

// Facet values are grouped case-insensitively ("UI" and "ui" are one chip); the first
// spelling seen wins as the label.
const facetKey = (v) => (v || "").trim().toLowerCase();

// The run modal's filter. `types`/`modules` are Sets of facet keys — an empty Set means
// "no filter on this row", so every case passes.
const filterCases = (cases, types, modules, query) => {
  const q = query.trim().toLowerCase();
  return cases.filter((c) =>
    (types.size === 0 || types.has(facetKey(c.test_type))) &&
    (modules.size === 0 || modules.has(facetKey(c.category))) &&
    (!q || `${c.name || ""} ${c.external_id || ""} ${c.url || ""}`.toLowerCase().includes(q)));
};

// Multi-select filter chips with live counts. Hidden when every case shares one value —
// a lone chip filters nothing and only takes up room.
function FilterChips({ label, options, value, onToggle }) {
  if (options.length < 2) return null;
  return (
    <div className="flex gap-2.5">
      <span className="w-14 shrink-0 pt-1 text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {options.map((o) => {
          const on = value.has(o.key);
          return (
            <button
              key={o.key} type="button" aria-pressed={on} onClick={() => onToggle(o.key)}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                on ? "bg-brand-600 text-white shadow-soft"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10")}
            >
              {o.label}
              <span className={cn("text-[0.625rem] tabular-nums", on ? "text-white/75" : "text-slate-400")}>{o.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// A PC mid-batch. It will take new work, but only after it has drained the batch
// it is on — so pre-selecting it silently turns "run now" into "run later".
const isBusy = (d) => !!d.current_run_id;

function RunTestCasesModal({ cases, onlineDevices, onClose, onStarted }) {
  const [picked, setPicked] = useState(() => new Set(cases.map((c) => c.id)));
  const freeDevices = onlineDevices.filter((d) => !isBusy(d));
  const [pickedDevices, setPickedDevices] = useState(
    // Free machines by default; a busy one is a deliberate choice, not a default.
    // Falls back to all when every PC is busy, so the batch still queues rather
    // than leaving the reader with nothing selected and no way to start.
    () => (freeDevices.length ? freeDevices : onlineDevices).map((d) => d.id),
  );
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState(() => new Set());
  const [modules, setModules] = useState(() => new Set());
  const [browser, setBrowser] = useState("chromium");
  // Fast mode is the default: one window per PC with a tab per test. It is the quickest
  // way to get through a sheet, and the login carries over between cases. The switch
  // states the trade-off (shared cookies, no per-test network log) in the open.
  const [fastMode, setFastMode] = useState(true);
  // Off by default, every time — this is a per-batch, deliberate choice, never a
  // remembered setting. A destructive case (changes real data — a password
  // reset, a delete) is otherwise parked as "blocked" unconditionally; ticking
  // this for THIS batch is the tester saying "yes, run it anyway".
  const [allowDestructive, setAllowDestructive] = useState(false);
  const [costCap, setCostCap] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Chips describe THIS catalog — built from the loaded cases, never a fixed list, so a
  // sheet that invents a new type shows up on its own.
  const facets = useMemo(() => {
    const build = (pick) => {
      const m = new Map();
      for (const c of cases) {
        const raw = (pick(c) || "").trim();
        if (!raw) continue;
        const key = raw.toLowerCase();
        const hit = m.get(key);
        if (hit) hit.count += 1;
        else m.set(key, { key, label: raw, count: 1 });
      }
      return [...m.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    };
    return { types: build((c) => c.test_type), modules: build((c) => c.category) };
  }, [cases]);

  const shown = useMemo(() => filterCases(cases, types, modules, query), [cases, types, modules, query]);
  // Only the SELECTED cases matter — no clutter on an ordinary batch that
  // happens to share the catalog with a destructive one nobody ticked.
  const destructivePickedCount = useMemo(
    () => cases.filter((c) => picked.has(c.id) && c.destructive).length,
    [cases, picked],
  );

  // Picking a filter also SETS the selection to what it shows, so nothing can stay
  // ticked out of sight and the footer count is always what will run. Typing in the
  // search box only narrows the view — it never drops a selection.
  const applyFilter = (nextTypes, nextModules, nextQuery = query) => {
    setTypes(nextTypes);
    setModules(nextModules);
    setPicked(new Set(filterCases(cases, nextTypes, nextModules, nextQuery).map((c) => c.id)));
  };
  const toggleFacet = (set, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };

  const shownPicked = shown.filter((c) => picked.has(c.id)).length;
  const hiddenPicked = picked.size - shownPicked;
  const allShownPicked = shown.length > 0 && shownPicked === shown.length;
  const filtering = types.size > 0 || modules.size > 0 || query.trim() !== "";
  const allDevices = onlineDevices.length > 0 && pickedDevices.length === onlineDevices.length;
  const busyCount = onlineDevices.length - freeDevices.length;

  const toggleShown = () => {
    const next = new Set(picked);
    for (const c of shown) { if (allShownPicked) next.delete(c.id); else next.add(c.id); }
    setPicked(next);
  };

  const toggleCase = (id, on) => {
    const next = new Set(picked);
    if (on) next.add(id); else next.delete(id);
    setPicked(next);
  };

  // A cap that isn't a positive number would be sent as null and run UNCAPPED — the
  // opposite of what was typed. Block the run and say so instead.
  const capRaw = costCap.trim();
  const capInvalid = capRaw !== "" && !(Number(capRaw) > 0);

  const start = async () => {
    setErr(""); setBusy(true);
    try {
      // How many cases a PC claims at a time is engine tuning, not a tester's decision —
      // the server picks it (and caps it), so it is no longer sent from here.
      const r = await createExeBatch({
        name: name.trim() || null,
        test_case_ids: [...picked],
        device_ids: pickedDevices,
        browser,
        fast_mode: fastMode,
        max_cost_usd: capRaw ? Number(capRaw) : null,
        auto_compile: true,
        allow_destructive: allowDestructive,
      });
      onStarted(r);
    } catch (e) {
      setErr(e.message || "Could not start the batch.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open onClose={onClose} title="Run test cases" size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">
            <b className="font-semibold text-slate-700 dark:text-slate-200">{picked.size}</b> case{picked.size === 1 ? "" : "s"}
            {" · "}<b className="font-semibold text-slate-700 dark:text-slate-200">{pickedDevices.length}</b> PC{pickedDevices.length === 1 ? "" : "s"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={busy || picked.size === 0 || pickedDevices.length === 0 || capInvalid} onClick={start}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Run on {pickedDevices.length} PC{pickedDevices.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <input aria-label="Batch name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Batch name (optional)" className={FIELD} />

        <div className="space-y-2.5 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
          <div className="relative">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, TC ID or URL…" aria-label="Search test cases"
              className={cn(FIELD, "bg-white py-2 pl-9 dark:bg-white/5")}
            />
          </div>

          <FilterChips
            label="Type" options={facets.types} value={types}
            onToggle={(key) => applyFilter(toggleFacet(types, key), modules)}
          />
          <FilterChips
            label="Module" options={facets.modules} value={modules}
            onToggle={(key) => applyFilter(types, toggleFacet(modules, key))}
          />

          <div className="flex min-h-[18px] items-center justify-between gap-2">
            <p className="text-[0.6875rem] text-slate-400">
              {!filtering ? "Picking a filter sets what runs. Tick rows to fine-tune."
                : hiddenPicked > 0
                  ? `Showing ${shown.length} of ${cases.length} · ${hiddenPicked} selected case${hiddenPicked === 1 ? "" : "s"} hidden by your search`
                  : `Showing ${shown.length} of ${cases.length} cases`}
            </p>
            {filtering && (
              <button type="button" className="shrink-0 text-xs font-medium text-brand-500 hover:underline"
                onClick={() => { setQuery(""); applyFilter(new Set(), new Set(), ""); }}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <p className="flex items-baseline gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Test cases
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                {picked.size} selected
              </span>
            </p>
            {shown.length > 0 && (
              <button type="button" className="shrink-0 text-xs font-medium text-brand-500 hover:underline" onClick={toggleShown}>
                {allShownPicked ? `Clear ${shown.length} shown` : `Select ${shown.length} shown`}
              </button>
            )}
          </div>
          <div className="max-h-56 space-y-1 overflow-auto rounded-xl border border-slate-200 p-1.5 dark:border-white/10">
            {shown.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-slate-400">No cases match these filters.</p>
            ) : shown.map((c) => {
              const on = picked.has(c.id);
              return (
                <label key={c.id} className={cn("flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm", on ? "bg-brand-50 dark:bg-brand-500/10" : "hover:bg-slate-50 dark:hover:bg-white/5")}>
                  <input type="checkbox" checked={on} onChange={(e) => toggleCase(c.id, e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{c.name}</span>
                  <span className="flex shrink-0 items-center gap-1.5 text-[0.625rem]">
                    {c.category && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500 dark:bg-white/10 dark:text-slate-300">{c.category}</span>}
                    {c.test_type && <span className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">{c.test_type}</span>}
                    {c.replayable && <span className="font-semibold text-emerald-500">⚡ free</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <p className="flex items-baseline gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Online PCs
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                {pickedDevices.length} selected
              </span>
              {/* Said before they press Run, not discovered afterwards from a
                  batch that sits at 0. */}
              {busyCount > 0 && (
                <span className="font-normal text-slate-400">
                  {freeDevices.length} free · {busyCount} busy
                </span>
              )}
            </p>
            {onlineDevices.length > 0 && (
              <button type="button" className="shrink-0 text-xs font-medium text-brand-500 hover:underline"
                onClick={() => setPickedDevices(allDevices ? [] : onlineDevices.map((d) => d.id))}>
                {allDevices ? "Clear all" : "Select all"}
              </button>
            )}
          </div>
          {onlineDevices.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400 dark:border-white/10">No online agents. Open the agent on a PC first.</p>
          ) : (
            <div className="max-h-32 space-y-1 overflow-auto rounded-xl border border-slate-200 p-1.5 dark:border-white/10">
              {onlineDevices.map((d) => {
                const on = pickedDevices.includes(d.id);
                return (
                  <label key={d.id} className={cn("flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm", on ? "bg-brand-50 dark:bg-brand-500/10" : "hover:bg-slate-50 dark:hover:bg-white/5")}>
                    <input type="checkbox" checked={on} onChange={(e) => setPickedDevices(e.target.checked ? [...pickedDevices, d.id] : pickedDevices.filter((x) => x !== d.id))} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                    <Server size={14} className={isBusy(d) ? "text-amber-500" : "text-emerald-500"} />
                    <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{devName(d)}</span>
                    {isBusy(d) && (
                      <span
                        className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                        title="Running another batch. Yours would start on this PC once that one finishes."
                      >
                        busy
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid gap-4 border-t border-slate-100 pt-4 dark:border-white/10 sm:grid-cols-3">
          <div>
            <span className="mb-1.5 block text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">Browser</span>
            <div className="inline-flex rounded-xl border border-slate-300 bg-slate-100 p-0.5 dark:border-white/10 dark:bg-white/5">
              {BROWSERS.map((b) => (
                <button
                  key={b} type="button" aria-pressed={browser === b} onClick={() => setBrowser(b)}
                  className={cn("rounded-[10px] px-2.5 py-1.5 text-xs font-medium capitalize transition-colors",
                    browser === b ? "bg-white text-slate-900 shadow-soft dark:bg-white/10 dark:text-white"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200")}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">Cost cap</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-400">$</span>
              <input
                value={costCap} onChange={(e) => setCostCap(e.target.value)} placeholder="no limit"
                inputMode="decimal" aria-label="Cost cap in dollars"
                className={cn(FIELD, "w-24 py-2 tabular-nums", capInvalid && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15")}
              />
            </div>
            {capInvalid && (
              <p className="mt-1 flex items-center gap-1 text-[0.6875rem] font-medium text-rose-600 dark:text-rose-300">
                <AlertTriangle size={12} /> Enter an amount above 0, or leave it blank.
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-500/25 dark:bg-amber-500/10 sm:col-span-3">
            <input type="checkbox" checked={fastMode} onChange={(e) => setFastMode(e.target.checked)} className="peer sr-only" />
            {/* Off-state colour must NOT use a `dark:` class here: Tailwind emits the
                dark utilities after the peer ones, so it would win over the checked
                state and the switch would read as OFF in dark mode. slate-400/40 is
                legible on both grounds. */}
            <span className="relative h-5 w-9 shrink-0 rounded-full bg-slate-400/40 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-amber-500 peer-checked:after:translate-x-4 peer-focus-visible:ring-4 peer-focus-visible:ring-amber-500/25" />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-amber-800 dark:text-amber-200">
                <Zap size={13} /> Fast mode — one window, a tab per test
              </span>
              <span className="mt-0.5 block text-[0.6875rem] text-slate-600 dark:text-slate-300">
                Quickest, and the login carries over. Tests share cookies, and the per-test network log is skipped.
              </span>
            </span>
          </label>

          {destructivePickedCount > 0 && (
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-rose-300 bg-rose-50/70 p-3 dark:border-rose-500/30 dark:bg-rose-500/10 sm:col-span-3">
              <input type="checkbox" checked={allowDestructive}
                    onChange={(e) => setAllowDestructive(e.target.checked)} className="peer sr-only" />
              <span className="relative h-5 w-9 shrink-0 rounded-full bg-slate-400/40 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-rose-600 peer-checked:after:translate-x-4 peer-focus-visible:ring-4 peer-focus-visible:ring-rose-500/25" />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-rose-800 dark:text-rose-200">
                  <AlertTriangle size={13} /> Include destructive test cases
                </span>
                <span className="mt-0.5 block text-[0.6875rem] text-slate-600 dark:text-slate-300">
                  {destructivePickedCount} of the selected case{destructivePickedCount === 1 ? "" : "s"} change{destructivePickedCount === 1 ? "s" : ""} real data
                  (e.g. resets a real password) and is normally parked as "blocked" instead of run. Off by default —
                  check this only when you deliberately want it to run this time.
                </span>
              </span>
            </label>
          )}
        </div>

        <p className="text-[0.6875rem] text-slate-400">
          Cases are shared out across the chosen PCs automatically — a faster PC simply runs more. Passing cases are saved, so running them again is free.
        </p>
        {err && <p className="flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-300"><AlertTriangle size={14} /> {err}</p>}
      </div>
    </Modal>
  );
}

function Chips({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.key} type="button" onClick={() => onChange(o.key)}
          className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
            value === o.key ? "bg-brand-500 text-white shadow-soft"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10")}
        >
          {o.label}{o.count != null ? ` (${o.count})` : ""}
        </button>
      ))}
    </div>
  );
}

// Shared batch list — the card UI behind BOTH the Running tab (every batch) and the
// Re-run tab (only batches that have been re-run). `baseFilter` picks the universe the
// chips filter within; `showRerun` surfaces each batch's re-run count on its row. The
// full per-run stats (cost, time, pass/fail) live in the expanded BatchDetail, so both
// tabs share exactly the same card.
// `searchable` is opt-in, and only the Running tab opts in. This component is
// shared with the Re-run tab, so defaulting it on would have added a search box
// to a section nobody asked for one in.
function BatchList({ baseFilter, showRerun = false, showDeleteAll = false, searchable = false,
                     searchPlaceholder = "Search…", emptyTitle, emptyMessage }) {
  const [batches, setBatches] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  // The query lives OUTSIDE the polled data, which is what keeps it applied
  // across the 3-second refresh below: `batches` is replaced wholesale every
  // tick, `query` is not, so the filter is simply re-applied to the new list.
  // Nothing is cached for searching — a batch that stops running leaves the API
  // response and therefore leaves the filtered view on the very next tick.
  const [query, setQuery] = useState("");
  const reload = useCallback(() => { listExeBatches(50).then(setBatches).catch(() => {}); }, []);
  useEffect(() => {
    reload();
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [reload]);

  const isRunning = (b) => b.status === "running" || b.status === "paused";
  const hasFail = (b) => b.failed + b.errored > 0;
  const universe = baseFilter ? batches.filter(baseFilter) : batches;
  const byChip = universe.filter((b) =>
    filter === "running" ? isRunning(b)
      : filter === "completed" ? b.status === "completed"
        : filter === "failures" ? hasFail(b)
          : filter === "passed" ? b.status === "completed" && !hasFail(b)
            : true);

  // Per-WORD matching against one joined haystack, the same rule DataTable uses:
  // every term must appear somewhere in the row, not the whole phrase in one
  // field. So "login failed" finds a batch named "Login" with a failed status,
  // and a trailing space while typing doesn't empty the list.
  //
  // The fields are the ones actually ON the row — the batch name, its id (so a
  // partial execution id finds it) and its status. `project_id` is deliberately
  // excluded: it is a bare UUID that the row never displays, so matching it
  // would make results appear for a query with nothing visible to explain it.
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (b) => {
    if (!terms.length) return true;
    const hay = [b.name || "Test-case batch", b.id, b.status].join("  ").toLowerCase();
    return terms.every((t) => hay.includes(t));
  };
  const shown = searchable ? byChip.filter(matches) : byChip;
  // Three different empty states, and they must not be confused: nothing at all,
  // nothing matching the chip, nothing matching the SEARCH.
  const hiddenBySearch = searchable && terms.length > 0 && byChip.length > 0 && shown.length === 0;
  const chips = [
    { key: "all", label: "All", count: universe.length },
    { key: "running", label: "Running", count: universe.filter(isRunning).length },
    { key: "passed", label: "All passed", count: universe.filter((b) => b.status === "completed" && !hasFail(b)).length },
    { key: "failures", label: "With failures", count: universe.filter(hasFail).length },
  ];

  return (
    <div className="space-y-4">
      {/* Same inline search box the Changelog, Support and Platform Ops pages
          use — icon inside a relative wrapper, h-9 rounded-xl, themed for both
          modes. `type="search"` gives the browser's own clear control, which is
          what those pages rely on too; Escape clears as well, so it is reachable
          from the keyboard alone. Full width on a phone, capped on a desktop, so
          it can never push the controls below it out of the card. */}
      {searchable && (
        <div className="relative w-full sm:max-w-sm">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape" && query) { e.preventDefault(); setQuery(""); } }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoComplete="off"
            className="h-9 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Chips value={filter} onChange={setFilter} options={chips} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={14} /> Refresh</Button>
          {showDeleteAll && (
            <DeleteAllButton
              disabled={!batches.length}
              title="Delete all batches?"
              message={`This stops any running batches (signalling their PCs) and permanently deletes all ${batches.length} batch${batches.length === 1 ? "" : "es"} for your team.`}
              onDelete={deleteAllExeBatches}
              onDone={reload}
            />
          )}
        </div>
      </div>
      {universe.length === 0 ? (
        <EmptyState icon={showRerun ? RotateCw : ListChecks} title={emptyTitle} message={emptyMessage} />
      ) : hiddenBySearch ? (
        // NOT "no batches" — there are batches, this search just doesn't match
        // any of them. Saying otherwise reads as "your runs vanished", which is
        // alarming and wrong.
        <EmptyState
          icon={Search}
          title={`No batches match “${query.trim()}”`}
          message="Check the spelling, or clear the search to see everything again."
        />
      ) : shown.length === 0 ? (
        <EmptyState icon={ListChecks} title="Nothing here" message="No batches match this filter." />
      ) : (
        <div className="card overflow-hidden">
          <ul className="divide-y divide-slate-100 dark:divide-white/5">
            {shown.map((b) => {
              const open = openId === b.id;
              const done = b.passed + b.failed + b.errored;
              const waiting = b.status === "running" && b.running === 0 && done === 0 && b.queued > 0;
              return (
                <li key={b.id}>
                  <button type="button" onClick={() => setOpenId(open ? null : b.id)} className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-left text-sm transition-colors hover:bg-slate-50 dark:hover:bg-white/5">
                    {waiting ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-300"><Clock size={13} /> Waiting</span>
                    ) : <ExeStatus status={b.status} />}
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="min-w-0 truncate font-medium text-slate-800 dark:text-white">{b.name || "Test-case batch"}</span>
                      {showRerun && b.rerun_count > 0 && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-500/10 px-2 py-0.5 text-[0.65rem] font-semibold text-brand-600 dark:text-brand-300" title={`Re-run ${b.rerun_count} time${b.rerun_count === 1 ? "" : "s"}`}>
                          <RotateCw size={10} /> ×{b.rerun_count}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-slate-400">
                      {done}/{b.total} done · <span className="text-emerald-600 dark:text-emerald-300">{b.passed}✓</span>
                      {b.failed + b.errored > 0 && <> · <span className="text-rose-600 dark:text-rose-300">{b.failed + b.errored}✗</span></>}
                      {!waiting && fmtElapsed(batchElapsed(b)) && <> · ⏱ {fmtElapsed(batchElapsed(b))}{b.completed_at ? "" : " so far"}</>}
                      {" · "}{fmt(b.created_at)}
                    </span>
                    <span className="text-xs font-medium text-brand-500">{open ? "Hide" : "View"}</span>
                  </button>
                  {open && <BatchDetail batchId={b.id} />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function RunningTab() {
  return (
    <BatchList
      showDeleteAll
      searchable
      searchPlaceholder="Search running test cases…"
      emptyTitle="No batches yet"
      emptyMessage="Import cases, then “Run test cases” to distribute them across your PCs."
    />
  );
}

// Re-run tab — only batches that have been re-run (dev-fix verification cycles). Each
// row shows how many times it was re-run; expand it for the current re-run's cost, time
// and pass/fail — the same card as Running. Its time and cost are measured from the
// latest re-run (backend `started_at`), not the batch's original creation.
function RerunTab() {
  return (
    <BatchList
      baseFilter={(b) => (b.rerun_count || 0) > 0}
      showRerun
      emptyTitle="No re-runs yet"
      emptyMessage="Re-run a finished batch from the Running tab (to verify dev-fixed and still-failing cases) — it then shows up here with its re-run count, cost and time."
    />
  );
}

function BatchDetail({ batchId }) {
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [itemFilter, setItemFilter] = useState("all");
  const [detailRun, setDetailRun] = useState(null);   // {runId, name} → opens the run detail
  const [confirmStop, setConfirmStop] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, it] = await Promise.all([getExeBatch(batchId), listExeBatchItems(batchId)]);
      setBatch(b); setItems(it);
    } catch (e) {
      /* ignore */
    }
  }, [batchId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  const control = async (fn) => {
    setBusy(true);
    try { await fn(batchId); await load(); } catch (e) { toastError(e?.message || "That action couldn't be completed."); } finally { setBusy(false); }
  };
  const doStop = async () => { await control(stopExeBatch); setConfirmStop(false); };

  if (!batch) return <div className="px-5 py-4 text-xs text-slate-400">Loading…</div>;
  const active = batch.status === "running" || batch.status === "paused";
  const done = batch.passed + batch.failed + batch.errored;
  const pct = batch.total ? Math.round((done / batch.total) * 100) : 0;
  const passedItems = items.filter((i) => i.status === "passed");
  // 'cancelled' (tester stopped the batch) is not a failure — keep it out of the Failed tab
  // so the count matches the header's real-failure tally (see recompute).
  const failedItems = items.filter((i) => i.status === "failed");
  // Keep this in lock-step with the backend's "errored" tally (recompute): error,
  // orphaned AND inconclusive all mean "couldn't get a clean verdict — needs a human",
  // so the tab count matches the header's "N needs attention".
  const attnItems = items.filter((i) => ["error", "orphaned", "inconclusive"].includes(i.status));
  const shownItems = itemFilter === "passed" ? passedItems
    : itemFilter === "failed" ? failedItems
      : itemFilter === "attention" ? attnItems : items;

  return (
    <div className="border-t border-slate-100 bg-slate-50/40 p-4 dark:border-white/10 dark:bg-white/5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
          <span>{done}/{batch.total} done ({pct}%)</span>
          <span className="text-emerald-600 dark:text-emerald-300">{batch.passed} passed</span>
          {batch.failed > 0 && <span className="text-rose-600 dark:text-rose-300">{batch.failed} failed</span>}
          {batch.errored > 0 && <span className="text-amber-600 dark:text-amber-300">{batch.errored} needs attention</span>}
          <span>⏳ {batch.queued} queued · ▶ {batch.running} running</span>
          {fmtElapsed(batchElapsed(batch)) && (
            <span>⏱ {batch.completed_at ? "took " : ""}{fmtElapsed(batchElapsed(batch))}{batch.completed_at ? "" : " so far"}</span>
          )}
          {batch.cost && batch.cost.cost_usd > 0 && <span>💲 {fmtCost({ cost_usd: batch.cost.cost_usd })} est.</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { downloadExeBatchReport(batchId).catch((e) => toastError(e?.message || "Couldn't download the report.")); }}><Download size={13} /> Download report</Button>
          {batch.status === "running" && <Button variant="outline" size="sm" disabled={busy} onClick={() => control(pauseExeBatch)}><Pause size={13} /> Pause</Button>}
          {batch.status === "paused" && <Button variant="outline" size="sm" disabled={busy} onClick={() => control(resumeExeBatch)}><Play size={13} /> Resume</Button>}
          {/* App dialog, not window.confirm — a native prompt ignores the theme
              and can be suppressed by the browser, which would silently turn Stop
              into a no-op. */}
          {active && <Button variant="outline" size="sm" disabled={busy} onClick={() => setConfirmStop(true)}><Square size={13} /> Stop</Button>}
          {/* Re-run only what needs it (dev-fixed + still failing) — see the preview. */}
          {!active && (
            <RerunBatchButton
              batch={batch}
              getPreview={getExeRerunPreview}
              rerun={rerunExeBatch}
              devicesApi={listExeDevices}
              onDone={load}
            />
          )}
        </div>
      </div>
      <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
        <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {batch.stalled && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          <WifiOff size={14} className="shrink-0" />
          <span className="min-w-0 flex-1">
            Stalled — every PC chosen for this batch is offline. Its remaining cases will resume when one comes back, or move them to the PCs online now.
          </span>
          <Button
            variant="outline" size="sm" disabled={busy}
            onClick={() => control(takeoverExeBatch)}
          >
            <Repeat size={13} /> Move to online PCs
          </Button>
        </div>
      )}
      {!batch.stalled && batch.status === "running" && batch.running === 0 && done === 0 && batch.queued > 0 && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
          <Clock size={14} className="shrink-0" /> Waiting for a PC to free up — this batch starts automatically when one finishes its current batch.
        </div>
      )}
      <div className="mb-2">
        <Chips value={itemFilter} onChange={setItemFilter} options={[
          { key: "all", label: "All", count: items.length },
          { key: "passed", label: "Passed", count: passedItems.length },
          { key: "failed", label: "Failed", count: failedItems.length },
          { key: "attention", label: "Needs attention", count: attnItems.length },
        ]} />
      </div>
      <div className="max-h-80 overflow-auto rounded-xl border border-slate-100 dark:border-white/10">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-slate-50 text-slate-400 dark:bg-ink-900">
            <tr>
              {/* The sheet's own reference. Without it a blocked or failed row can only
                  be named by its title, and two cases on one screen often share one
                  ("Search by reviewer name" / "Search with no matches") — so a reader
                  could not tell WHICH case to go and look at in the sheet. */}
              <th className="px-3 py-2 font-medium">TC ID</th>
              <th className="px-3 py-2 font-medium">Test case</th>
              <th className="px-3 py-2 font-medium">PC</th>
              <th className="px-3 py-2 font-medium">Status</th>
              {/* What the assigned developer says about this case — a fix they've
                  claimed shows here and is what the next Re-run verifies. */}
              <th className="px-3 py-2 font-medium">Developer</th>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {shownItems.map((it) => {
              const clickable = !!it.run_id;
              return (
                <tr
                  key={it.id}
                  onClick={() => clickable && setDetailRun({ runId: it.run_id, name: it.name })}
                  className={cn("text-slate-700 dark:text-slate-200", clickable && "cursor-pointer hover:bg-slate-100/70 dark:hover:bg-white/10")}
                >
                  {/* Never blank: the sheet's reference when it has one, else the
                      item's own id — the same order the downloaded report uses, so the
                      two can be read side by side. */}
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {it.external_id || (it.id || "").slice(0, 8)}
                  </td>
                  <td className="max-w-[16rem] truncate px-3 py-2">{it.name}{it.attempt > 1 ? <span className="text-slate-400"> · try {it.attempt}</span> : ""}</td>
                  <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{it.device || "—"}</td>
                  <td className="px-3 py-2"><ExeStatus status={it.status} /></td>
                  <td className="px-3 py-2"><DevStatusCell item={it} /></td>
                  <td className="px-3 py-2 text-slate-400">{fmtDur(it.duration_seconds)}</td>
                  <td className="px-3 py-2 text-right">{clickable && <span className="font-medium text-brand-500">View</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detailRun && (
        <Modal open onClose={() => setDetailRun(null)} title={detailRun.name} size="xl">
          <LiveRun runId={detailRun.runId} />
        </Modal>
      )}
      <ConfirmDialog
        open={confirmStop}
        title="Stop this batch?"
        body="Tests that are currently running are cancelled and the remaining queued cases won't run. You can re-run the batch afterwards."
        confirmLabel="Stop batch"
        busy={busy}
        onConfirm={doStop}
        onCancel={() => setConfirmStop(false)}
      />
    </div>
  );
}
